/**
 * Operator login — the only public screen. The operator pastes their **8x8 SSO
 * token** (the same Bearer token their browser already holds for any 8x8 app); we
 * clean it up — you can paste a bare token, a `Bearer …`, a whole copied
 * `authorization: Bearer …` header block, or even a cookie dump — extract the JWT,
 * and exchange it for a session. The tenant is whatever the token says (its `cid`).
 */

import { useMemo, useState } from 'react';
import styled from 'styled-components';
import Button from '@8x8/oxygen-button';
import type { Environment } from '@spaces/contracts';
import { getSession, login } from '../api';
import type { SessionState } from '../api';
import { navigate, safeNext, useLocation } from '../router';
import { EnvTag, Notice } from '../ui/primitives';
import { Logo } from '../ui/Logo';

const Screen = styled.div`
    height: 100%;
    background: ${p => p.theme.ui31};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
`;

const LoginCard = styled.form`
    width: 460px;
    max-width: 100%;
    background: ${p => p.theme.ui06};
    border: 1px solid ${p => p.theme.ui01};
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 32px;
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    color: ${p => p.theme.textColor01};
`;

const Title = styled.div`
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    color: ${p => p.theme.textColor01};
`;

const Sub = styled.div`
    font-size: 14px;
    line-height: 20px;
    color: ${p => p.theme.textColor02};
    margin-top: 4px;
`;

const Label = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: ${p => p.theme.textColor02};
    margin-bottom: 6px;
`;

const TokenArea = styled.textarea`
    width: 100%;
    min-height: 132px;
    resize: vertical;
    box-sizing: border-box;
    padding: 10px 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    line-height: 1.5;
    color: ${p => p.theme.textColor01};
    background: ${p => p.theme.ui31};
    border: 1px solid ${p => p.theme.ui01};
    border-radius: 6px;
    outline: none;
    &:focus { border-color: #4687ed; }
`;

const Found = styled.div`
    font-size: 12px;
    line-height: 18px;
    color: ${p => p.theme.textColor02};
    margin-top: 8px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    word-break: break-all;
`;

/** Pull a JWT out of a messy paste (bare token, `Bearer …`, header block, or cookies). */
function extractJwt(raw: string): string | null {
    const match = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/.exec(raw ?? '');

    return match ? match[0] : null;
}

export function Login({
    env,
    onSession
}: {
    env: Environment;
    onSession: (s: SessionState) => void;
}): JSX.Element {
    const route = useLocation();
    const expired = route.query.get('expired') === '1';
    const next = safeNext(route.query.get('next'));

    const [raw, setRaw] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const extracted = useMemo(() => extractJwt(raw), [raw]);

    const onSubmit = async (ev: React.FormEvent): Promise<void> => {
        ev.preventDefault();
        setError(null);

        const token = extractJwt(raw);

        if (!token) {
            setError('No token found in what you pasted. Paste your 8x8 “Authorization: Bearer …” header (or the token itself).');

            return;
        }

        setBusy(true);
        const result = await login(token);

        if (!result.ok) {
            setBusy(false);
            // The server returns the precise reason (expired / audience / signature …)
            // — it's the operator's own token, so surfacing it is a help, not a leak.
            setError(result.message);

            return;
        }

        const session = await getSession();

        if (session.ok) {
            onSession(session.data);
        }

        navigate(next, true);
    };

    return (
        <Screen>
            <LoginCard onSubmit={onSubmit}>
                <Head>
                    <Logo height={26} />
                    <EnvTag env={env} />
                </Head>
                <Title>Rooms Admin</Title>
                <Sub>Sign in with your 8x8 token to manage Spot rooms for your tenant.</Sub>

                {expired ? (
                    <div style={{ marginTop: 16 }}>
                        <Notice kind="info">Your session expired — sign in to continue.</Notice>
                    </div>
                ) : null}
                {error ? (
                    <div style={{ marginTop: 16 }}>
                        <Notice kind="warning">{error}</Notice>
                    </div>
                ) : null}

                <div style={{ marginTop: 20 }}>
                    <Label htmlFor="token">Your 8x8 token</Label>
                    <TokenArea
                        id="token"
                        value={raw}
                        autoFocus
                        spellCheck={false}
                        placeholder="Paste your Authorization: Bearer header, the token, or a cookie dump — we'll pick out the token."
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRaw(e.target.value)}
                    />
                    {raw.trim().length > 0 ? (
                        extracted
                            ? <Found>✓ Token detected: {extracted.slice(0, 24)}…{extracted.slice(-12)}</Found>
                            : <Found>No token found yet — paste the “Bearer …” value.</Found>
                    ) : null}
                </div>

                <div style={{ marginTop: 18 }}>
                    <Button type="submit" variant="primary" isDisabled={busy} style={{ width: '100%', justifyContent: 'center' }}>
                        {busy ? 'Verifying…' : 'Sign in'}
                    </Button>
                </div>
            </LoginCard>
        </Screen>
    );
}
